import React, { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import { useTodayItems, calendarItemDay } from '../../hooks/useTodayItems';
import { CALENDAR_ITEM_COLORS } from '../../types/calendar';

/**
 * "Upcoming Events" card on the classic (non-admin) dashboard.
 *
 * Rebuilt on `useTodayItems` — the same composer the calendar and the home rail
 * use. The hand-rolled version this replaces had four defects that all vanish by
 * construction here:
 *
 *  - It only handled MONTHLY, YEARLY and one-off events, so every DAILY and
 *    WEEKLY recurring event was silently dropped.
 *  - Its refresh path re-derived dates with `event.start.split('-')`, which on a
 *    TIMED event ('2026-08-03T09:00:00.000Z') produced an Invalid Date and threw
 *    a RangeError out of date-fns' format().
 *  - It read the WHOLE events collection with an unbounded getDocs on mount and
 *    again after every delete.
 *  - It never filtered soft-deleted events, so once the calendar moved to
 *    `status: 'cancelled'` deletes, cancelled events would have reappeared here.
 *
 * Clicking a row opens that day on the calendar rather than the edit modal. The
 * modal needs a base document plus an occurrence handle to edit a recurring
 * series safely, and a summary card is the wrong place to get that subtle.
 */
const EVENT_WINDOW_DAYS = 60;
const MAX_ROWS = 50;

const EventFeed: React.FC = () => {
  const navigate = useNavigate();

  // Events only — this card has always been "Upcoming Events", not a money feed.
  const { items, loading } = useTodayItems({
    days: EVENT_WINDOW_DAYS,
    filters: {
      showEvents: true,
      showInvoices: false,
      showRecurringInvoices: false,
      showRecurringExpenses: false,
      showJobs: false,
    },
  });

  const rows = useMemo(
    () =>
      items
        .filter(i => i.type === 'event')
        .slice(0, MAX_ROWS)
        .map(item => ({
          key: item.id,
          day: calendarItemDay(item),
          title: item.title || '(untitled event)',
          description: item.description || '',
          colour: item.color || CALENDAR_ITEM_COLORS.event,
        })),
    [items]
  );

  /** 'YYYY-MM-DD' -> "Aug 03". Built as local noon so no parse can shift the day. */
  const formatDay = (day: string) => {
    const d = new Date(`${day}T12:00:00`);
    return isNaN(d.getTime()) ? day : format(d, 'MMM dd');
  };

  const openDay = (day: string) => navigate(`/calendar?date=${day}`);

  if (loading) {
    return (
      <>
        <div className='card-header border-0 pt-5'>
          <h3 className='card-title align-items-start flex-column'>
            <span className='card-label fw-bold fs-3 mb-1'>Upcoming Events</span>
          </h3>
        </div>
        <div className='card-body pt-5'>
          <div className='text-center text-muted'>Loading…</div>
        </div>
      </>
    );
  }

  return (
    <>
      <div className='card-header border-0 pt-5'>
        <h3 className='card-title align-items-start flex-column'>
          <span className='card-label fw-bold fs-3 mb-1'>Upcoming Events</span>
          <span className='text-muted mt-1 fw-semibold fs-7'>
            Next {rows.length} event{rows.length === 1 ? '' : 's'}
          </span>
        </h3>
      </div>

      <div className='card-body pt-5' style={{ maxHeight: '1250px', overflowY: 'auto' }}>
        {rows.length === 0 ? (
          <div className='text-center text-muted'>No upcoming events found</div>
        ) : (
          <div className='timeline-label'>
            {rows.map((row, index) => {
              const showDate = index === 0 || row.day !== rows[index - 1].day;
              return (
                <div
                  className='timeline-item'
                  key={row.key}
                  onClick={() => openDay(row.day)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      openDay(row.day);
                    }
                  }}
                  role='button'
                  tabIndex={0}
                  title={`Open ${row.day} on the calendar`}
                  style={{ cursor: 'pointer' }}
                >
                  <div className='timeline-label fw-bold text-gray-800 fs-6'>
                    {showDate ? formatDay(row.day) : ''}
                  </div>

                  <div className='timeline-badge'>
                    <i className='fa fa-genderless fs-1' style={{ color: row.colour }}></i>
                  </div>

                  <div className='fw-mibold timeline-content ps-3'>
                    <div className='pe-3'>
                      <div className='fs-5 fw-bold'>{row.title}</div>
                      {row.description && (
                        <div className='text-muted fw-semibold mt-1 fs-7'>{row.description}</div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </>
  );
};

export default EventFeed;
